import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Select,
  MenuItem,
} from '@mui/material';
import { FormatListBulleted as ListIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WidgetCard from '../WidgetCard';
import { SiteScheduleService } from '../../../services/SiteScheduleService';
import {
  SiteSchedule,
  ScheduleEventType,
  SCHEDULE_EVENT_TYPES,
  MEMO_EVENT_TYPE,
  EVENT_TYPE_LABEL,
  SCHEDULE_STATUS_LABEL,
  compareByScheduleTime,
  hasScheduleDate,
} from '../../../models/SiteSchedule';

interface ScheduleWidgetProps {
  projectId: number;
  /**
   * 액션 버튼 클릭 시 부모가 처리할 콜백. 지정 시 페이지 이동 대신
   * 부모가 모달을 띄우는 등의 동작이 가능하다. 미지정 시 기존처럼
   * 캘린더 페이지로 navigate.
   */
  onActionClick?: () => void;
  /** 일정 행 클릭 시 상세 모달을 띄우는 콜백 (개요에서 바로 보기). */
  onItemClick?: (schedule: SiteSchedule) => void;
}

const EVENT_COLOR_NAME: Record<ScheduleEventType, 'default' | 'primary' | 'info' | 'warning' | 'error' | 'secondary'> = {
  visit: 'primary',
  maintenance: 'info',
  incident: 'error',
  meeting: 'secondary',
  worklog: 'warning',
  memo: 'default',
  other: 'default',
};

// 일정 등록 폼의 유형 칩과 동일한 목록 (ScheduleFormDialog 의
// SCHEDULE_ONLY_EVENT_TYPES 와 같은 규칙 — 메모는 등록 폼에서 유형이 아니라
// 별도 토글이므로 필터에도 넣지 않는다).
const FILTER_EVENT_TYPES = SCHEDULE_EVENT_TYPES.filter((t) => t !== MEMO_EVENT_TYPE);

const MAX_ROWS = 5;
/** 담당자 필터의 "미지정" sentinel — 실제 담당자 이름과 충돌하지 않는 값. */
const NO_ASSIGNEE = '__none__';

const formatDayLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.substring(0, 10);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return '오늘';
  if (diffDays === 1) return '내일';
  if (diffDays < 0) return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  return `${diffDays}일 후`;
};

/** 시점이 오늘 0시 이전인가 — 지난 업무는 날짜를 붉게 표시. */
const isPastDay = (iso: string): boolean => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return t < today.getTime();
};

/**
 * 개요의 "업무 리스트" 위젯 — 미완료(취소·완료 제외) 업무를 날짜순으로 보여준다.
 *
 * 이전 "다가오는 일정"은 from=오늘 로 조회해서 지난 날짜로 등록한 업무와 날짜
 * 없는 메모가 서버에서 걸러져 아예 안 보였다. 업무 리스트는 기간 제한 없이
 * 전부 가져와 상태만 거른다 — 지난 업무는 날짜가 붉게 표시되어 밀린 일이 보인다.
 */
const ScheduleWidget: React.FC<ScheduleWidgetProps> = ({ projectId, onActionClick, onItemClick }) => {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState<SiteSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | ScheduleEventType>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');

  useEffect(() => {
    let cancelled = false;
    SiteScheduleService.list(projectId)
      .then((data) => {
        if (!cancelled) setSchedules(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // 담당자 필터 옵션 — 미완료 업무에 실제로 등장하는 이름만.
  const assignees = useMemo(() => {
    const set = new Set<string>();
    schedules.forEach((s) => {
      if (s.status !== 'cancelled' && s.status !== 'done' && s.assignee) set.add(s.assignee);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [schedules]);

  const { visible, filteredCount } = useMemo(() => {
    const filtered = schedules
      .filter((s) => s.status !== 'cancelled' && s.status !== 'done')
      .filter((s) => typeFilter === 'all' || s.event_type === typeFilter)
      .filter((s) => {
        if (assigneeFilter === 'all') return true;
        if (assigneeFilter === NO_ASSIGNEE) return !s.assignee;
        return s.assignee === assigneeFilter;
      })
      // 날짜순, 날짜 없는 메모는 뒤로 (compareByScheduleTime 규칙).
      .sort((a, b) => compareByScheduleTime(a, b, 'asc'));
    return { visible: filtered.slice(0, MAX_ROWS), filteredCount: filtered.length };
  }, [schedules, typeFilter, assigneeFilter]);

  const filterSx = {
    fontSize: '0.72rem',
    height: 26,
    '& .MuiSelect-select': {
      py: 0.25,
      px: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  } as const;

  return (
    <WidgetCard
      title={`업무 리스트 ${filteredCount > 0 ? `(${filteredCount}건)` : ''}`}
      icon={<ListIcon fontSize="small" />}
      actionLabel="전체 보기"
      onAction={onActionClick ?? (() => navigate(`/project/${projectId}/schedules`))}
      testId="widget-schedule"
    >
      {/* 좁은 폭(모바일·좁게 배치된 셀)에서는 두 필터가 줄바꿈되게. */}
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
        <Select
          size="small"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as 'all' | ScheduleEventType)}
          sx={filterSx}
          aria-label="유형 필터"
        >
          <MenuItem value="all">유형 전체</MenuItem>
          {FILTER_EVENT_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {EVENT_TYPE_LABEL[t]}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
          // 긴 담당자 이름이 위젯 폭을 밀어내지 않게 상한 (말줄임은 filterSx).
          sx={{ ...filterSx, maxWidth: 150 }}
          aria-label="담당자 필터"
        >
          <MenuItem value="all">담당자 전체</MenuItem>
          <MenuItem value={NO_ASSIGNEE}>미지정</MenuItem>
          {assignees.map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : visible.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {schedules.length === 0 ? '등록된 업무가 없습니다.' : '조건에 맞는 업무가 없습니다.'}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {visible.map((s) => {
            const dated = hasScheduleDate(s);
            const past = dated && isPastDay(s.scheduled_at!);
            return (
              <Box
                key={s.id}
                onClick={onItemClick ? () => onItemClick(s) : undefined}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  ...(onItemClick && {
                    cursor: 'pointer',
                    borderRadius: 1,
                    mx: -0.5,
                    px: 0.5,
                    '&:hover': { bgcolor: 'action.hover' },
                  }),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ minWidth: 56, color: past ? 'error.main' : 'text.secondary' }}
                >
                  {dated ? formatDayLabel(s.scheduled_at!) : '—'}
                </Typography>
                <Chip
                  label={EVENT_TYPE_LABEL[s.event_type]}
                  size="small"
                  color={EVENT_COLOR_NAME[s.event_type] ?? 'default'}
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.title}
                </Typography>
                {s.assignee && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {s.assignee}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {SCHEDULE_STATUS_LABEL[s.status]}
                </Typography>
              </Box>
            );
          })}
          {filteredCount > visible.length && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ pl: '64px' }}
            >
              … 외 {filteredCount - visible.length}건
            </Typography>
          )}
        </Stack>
      )}
    </WidgetCard>
  );
};

export default ScheduleWidget;
